import { ShipmentMethodOption } from "@redotech/redo-customer-portal-shared/shipment-method/util";
import {
  HappyReturnsLocation,
  ShipmentMethod,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import PackageIcon from "@redotech/redo-web/arbiter-icon/package.svg";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { ExternalLink } from "@redotech/redo-web/link";
import { RadioGroup } from "@redotech/redo-web/radio";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import HappyReturnsLogo from "../../images/happyreturns.png?url";
import * as happyReturnsCss from "./happy-returns.module.css";

export const HappyReturns = memo(function HappyReturns({
  options,
  onCancel,
  selectMethod,
}: {
  options: ShipmentMethodOption[];
  onCancel(): void;
  selectMethod(method: ShipmentMethod): void;
}) {
  const [selected, setSelected] = useState(options[0]);

  return (
    <Flex align="center" dir="column" gap="lg">
      <div>
        <Text fontSize="xl" fontWeight="bold">
          How do you want to return your items?
        </Text>
        <Text fontSize="md" fontWeight="regular">
          Pick the method that works best for your return.
        </Text>
      </div>
      <div className={happyReturnsCss.shipmentMethodList}>
        <RadioGroup
          alignDescription
          bordered
          centerBubble={false}
          optionClassName={happyReturnsCss.shipmentMethodOption}
          optionLabel={(value: ShipmentMethodOption) => {
            switch (value.type) {
              case ShipmentMethodName.HAPPY_RETURNS:
                return (
                  <HappyReturnsOption
                    selected={value === selected}
                    {...value.happyReturnsDetails!}
                  />
                );
              case ShipmentMethodName.SELF_SHIP:
                return <SelfShipOption />;
              default:
                return null;
            }
          }}
          options={options}
          rightAligned
          value={selected}
          valueChange={setSelected}
        />
      </div>
      <div className={happyReturnsCss.buttonFooter}>
        <Flex justify="flex-end">
          <Button onClick={onCancel} theme={ButtonTheme.OUTLINED}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              selected.type !== ShipmentMethodName.PICKUP &&
              selectMethod({ method: selected.type })
            }
            theme={ButtonTheme.PRIMARY}
          >
            Next
          </Button>
        </Flex>
      </div>
    </Flex>
  );
});

const HappyReturnsOption = memo(function HappyReturnsOption({
  selected,
  locations,
  retailerId,
  zipCode,
}: {
  selected: boolean;
  locations: HappyReturnsLocation[];
  retailerId: string;
  zipCode: string;
}) {
  const showLocations = useCallback(() => {
    open(
      `https://locations.happyreturns.com/?address=${zipCode}&has_qr_code=true&retailer=${retailerId}`,
      "_blank",
    );
  }, [zipCode, retailerId]);

  return (
    <>
      <Flex align="center">
        <img
          alt="Happy Returns"
          className={happyReturnsCss.happyReturnsLogo}
          src={HappyReturnsLogo}
        />
        <div>
          <Text fontSize="sm" fontWeight="medium">
            Drop off at any Happy Returns® location
          </Text>
          <Text fontSize="sm" fontWeight="regular">
            No need for packaging or shipping
          </Text>
        </div>
      </Flex>
      {selected ? (
        <Flex className={happyReturnsCss.happyReturnsDetails} dir="column">
          {locations.length > 0 ? (
            <div>
              <Text fontSize="sm" fontWeight="semibold" mb="lg">
                Closest Happy Returns® locations:
              </Text>
              <Flex dir="column" gap="lg">
                {locations.map((location, i) => {
                  const { address, city, state, zipcode } = location.address;

                  const locationAddress = [
                    address,
                    city,
                    `${state} ${zipcode}`.trim(),
                  ]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <Flex gap="4xl" justify="space-between" key={i}>
                      <div>
                        <ExternalLink
                          url={`https://www.google.com/maps/place/${locationAddress.replaceAll(/\s+/g, "+")}/@${location.lat},${location.lng}`}
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            {location.name}
                          </Text>
                        </ExternalLink>
                        <Text fontSize="sm" fontWeight="regular">
                          {locationAddress}
                        </Text>
                      </div>
                      <Text
                        className={happyReturnsCss.distanceText}
                        fontSize="sm"
                        fontWeight="regular"
                      >
                        {location.distance.toFixed(1)} mi
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </div>
          ) : null}
          <div>
            <Button
              className={happyReturnsCss.happyReturnsLocationsButton}
              onClick={showLocations}
              size={ButtonSize.EXTRA_SMALL}
              theme={ButtonTheme.OUTLINED}
            >
              See all locations
            </Button>
          </div>
          <Text fontSize="sm" fontWeight="regular">
            By selecting you are agreeing to Happy Returns®{" "}
            <a
              href="https://www.privacypolicy.happyreturns.com/"
              rel="noreferrer"
              target="_blank"
            >
              privacy policy
            </a>
            .
          </Text>
        </Flex>
      ) : null}
    </>
  );
});

const SelfShipOption = memo(function SelfShipOption() {
  return (
    <Flex align="center">
      <div className={happyReturnsCss.shippingMethodIcon}>
        <PackageIcon height={18} width={18} />
      </div>
      <div>
        <Text fontSize="sm" fontWeight="medium">
          Box and ship it back to us
        </Text>
        <Text fontSize="sm" fontWeight="regular">
          Print a shipping label and bring it to a carrier
        </Text>
      </div>
    </Flex>
  );
});
